/**
 * Textos fijos en inglés de EE.UU. que NO pasan por el i18n (no hay `t`): las
 * listas de los paneles informativos de los formularios del builder
 * (`src/app/BuilderRenderer.tsx`) y su copia indexable en el HTML del servidor
 * (`src/components/seo/*SeoContent.tsx`). Un solo lugar para que lo que se ve y
 * lo que se emite digan lo mismo.
 *
 * Solo para tenants de EE.UU. (`forcedSiteLanguage(country) === 'en'`). La
 * automotora chilena con default_language 'en' NO pasa por acá: sigue con los
 * literales en español de siempre.
 *
 * Donde el `embed` del i18n en inglés ya dice lo que un concesionario de EE.UU.
 * diría, se reutiliza; donde no (los requisitos del crédito), va el texto propio.
 */
import { financingEn } from '@/i18n/locales/en/financing';
import { consignmentsEn } from '@/i18n/locales/en/consignments';
import { buyDirectEn } from '@/i18n/locales/en/buyDirect';
import { weSearchForYouEn } from '@/i18n/locales/en/weSearchForYou';
import { forcedSiteLanguage } from '@/lib/country';

type Paso = { title: string; desc: string };

export const EMBEDS_US = {
  financing: {
    infoTitle: financingEn.embed.infoTitle,
    benefitsTitle: 'Benefits',
    benefits: [...financingEn.embed.benefits] as string[],
    requirementsTitle: financingEn.embed.requirementsTitle,
    requirements: ["Driver's license", 'Proof of income', 'Proof of residence', 'Employment history'],
    contactTitle: 'Contact us directly',
  },
  consignments: {
    infoTitle: consignmentsEn.embed.infoTitle,
    steps: consignmentsEn.embed.steps.map((p) => ({ ...p })) as Paso[],
    benefitsTitle: consignmentsEn.embed.benefitsTitle,
    benefits: [...consignmentsEn.embed.benefits] as string[],
  },
  buyDirect: {
    infoTitle: buyDirectEn.embed.infoTitle,
    steps: buyDirectEn.embed.steps.map((p) => ({ ...p })) as Paso[],
    /** Modo tasador: el 1 y el 4 son los del modo visitante; el 2 y el 3, los del modo. */
    stepsTasador: [
      { ...buyDirectEn.embed.steps[0] },
      { title: 'Get an estimate', desc: "We'll email you an estimated purchase price for your car." },
      { title: 'We make you an offer', desc: "We'll schedule an in-person appraisal and give you a firm offer." },
      { ...buyDirectEn.embed.steps[3] },
    ] as Paso[],
    benefitsTitle: buyDirectEn.embed.benefitsTitle,
    benefits: [...buyDirectEn.embed.benefits] as string[],
  },
  weSearch: {
    infoTitle: weSearchForYouEn.embed.infoTitle,
    steps: weSearchForYouEn.embed.steps.map((p) => ({ ...p })) as Paso[],
    benefitsTitle: weSearchForYouEn.embed.benefitsTitle,
    benefits: [...weSearchForYouEn.embed.benefits] as string[],
  },
};

/**
 * Otros textos fijos de EE.UU. que no pueden salir del diccionario `en` porque
 * ese lo comparte la automotora chilena con el selector en inglés (sus montos
 * son pesos, su clasificación laboral es la chilena).
 */
export const TEXTOS_US = {
  /** Ejemplo del presupuesto en "Car Finder": dólares, no pesos chilenos. */
  budgetPlaceholder: 'e.g., 25,000',
  /** "1 vehicles found" → singular. Con más de uno sigue el `found` del diccionario. */
  vehicleFound: 'vehicle found',
  /**
   * Etiqueta VISIBLE de cada tipo de empleo del formulario de financiamiento.
   * Lo que se guarda en el lead y viaja en el correo NO cambia: sigue siendo el
   * texto de `financing.employmentTypes.*` (ver `opcionesDeEmpleo`).
   */
  employmentTypes: {
    employeeNonProfessional: 'Employed (hourly)',
    employeeProfessional: 'Employed (salaried)',
    selfEmployedNonProfessional: 'Self-employed (contractor)',
    selfEmployedProfessional: 'Self-employed (business owner)',
    retired: 'Retired',
    other: 'Other',
  },
};

/**
 * "N vehicles found": el texto que va después del número. EE.UU. con un solo
 * auto, en singular; cualquier otro caso, el `found` del diccionario tal cual.
 */
export const textoEncontrados = (cantidad: number, delDiccionario: string, country?: string | null): string =>
  cantidad === 1 && forcedSiteLanguage(country) === 'en' ? TEXTOS_US.vehicleFound : delDiccionario;

/** Claves de `financing.employmentTypes`, en el orden del select. */
export const CLAVES_EMPLEO = [
  'employeeNonProfessional',
  'employeeProfessional',
  'selfEmployedNonProfessional',
  'selfEmployedProfessional',
  'retired',
  'other',
] as const;

/**
 * Opciones del select "Actividad laboral" del formulario de financiamiento.
 * `value` es lo que se guarda en el lead (`financing_data.employment_type`) y
 * va en el correo: el texto del diccionario, igual que siempre. `label` es lo
 * que se ve: el mismo texto en todos los países, salvo EE.UU., que ve la
 * clasificación de allá (la chilena de dependiente/independiente profesional
 * no existe en EE.UU.).
 */
export const opcionesDeEmpleo = (
  t: (clave: string) => string,
  enUsa: boolean
): Array<{ value: string; label: string }> =>
  CLAVES_EMPLEO.map((clave) => {
    const value = t(`financing.employmentTypes.${clave}`);
    return { value, label: enUsa ? TEXTOS_US.employmentTypes[clave] : value };
  });
